import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import { analyticsAPI } from '../../services/api';
import { Activity, Zap } from 'lucide-react';

const ClientAnalytics = ({ userId, coachId }) => {
  const [trends, setTrends] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [trendsRes, interventionsRes] = await Promise.all([
          analyticsAPI.getTrends({ user_id: userId, coach_id: coachId }),
          analyticsAPI.getInterventions({ user_id: userId, coach_id: coachId })
        ]);
        
        // Handle potential wrapped response { stats: [...] } or direct array
        const trendsData = trendsRes.data?.trends || trendsRes.data?.stats || (Array.isArray(trendsRes.data) ? trendsRes.data : []);
        const interventionsData = interventionsRes.data?.interventions || interventionsRes.data?.stats || (Array.isArray(interventionsRes.data) ? interventionsRes.data : []);
        
        setTrends(trendsData);
        setInterventions(interventionsData);
      } catch (error) {
        console.error('Error fetching client analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, coachId]);

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tangerine"></div>
    </div>
  );

  const safeTrends = Array.isArray(trends) ? trends : [];
  const safeInterventions = Array.isArray(interventions) ? interventions : [];

  return (
    <div className="space-y-8 mt-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Emotional Trends */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-[var(--tangerine)]/10 rounded-lg">
              <Activity className="w-4 h-4 text-tangerine" />
            </div>
            <h4 className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-widest">
              Emotional State Trend
            </h4>
          </div>
          <div className="h-48 bg-[var(--off-white)] p-4 rounded-2xl border border-[var(--border-light)] shadow-inner">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={safeTrends}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-light)" opacity={0.5} />
                <XAxis 
                  dataKey="date" 
                  hide
                />
                <YAxis 
                  domain={[1, 5]} 
                  ticks={[1, 2, 3, 4, 5]}
                  axisLine={false} 
                  tickLine={false} 
                  fontSize={10} 
                  width={20}
                  tick={{ fill: 'var(--text-secondary)' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: 'none', 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    backgroundColor: 'var(--surface-white)',
                    color: 'var(--text-primary)'
                  }}
                  itemStyle={{ color: 'var(--text-primary)' }}
                  labelFormatter={(str) => {
                    try {
                      return new Date(str).toLocaleDateString();
                    } catch (e) {
                      return str;
                    }
                  }}
                  formatter={(value, name, props) => [value, props.payload.label]}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="var(--tangerine)" 
                  strokeWidth={3} 
                  dot={{ r: 4, fill: 'var(--tangerine)', strokeWidth: 2, stroke: 'var(--surface-white)' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Interventions used for this user */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-[var(--tangerine)]/10 rounded-lg">
              <Zap className="w-4 h-4 text-tangerine" />
            </div>
            <h4 className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-widest">
              Applied Techniques
            </h4>
          </div>
          <div className="h-48 bg-[var(--off-white)] p-4 rounded-2xl border border-[var(--border-light)] shadow-inner">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={safeInterventions} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border-light)" opacity={0.5} />
                <XAxis type="number" hide />
                <YAxis 
                  dataKey="label" 
                  type="category" 
                  axisLine={false} 
                  tickLine={false} 
                  fontSize={10} 
                  width={100} 
                  tick={{ fill: 'var(--text-secondary)' }}
                />
                <Tooltip 
                  cursor={{ fill: 'var(--tangerine)', opacity: 0.05 }}
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: 'none', 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    backgroundColor: 'var(--surface-white)',
                    color: 'var(--text-primary)'
                  }}
                  itemStyle={{ color: 'var(--text-primary)' }}
                />
                <Bar dataKey="count" fill="var(--tangerine)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientAnalytics;
